
import { v4 as uuidv4 } from 'uuid';
import { BackupData } from '../../../services/backupService';
import { GrandTotalParseResult } from '../parsers/grandTotalParser';
import { Contact, Invoice, Quote, CatalogItem, LineItem } from '../../../types';

export const mapGrandTotalData = (parsed: GrandTotalParseResult): BackupData => {
    const backup: BackupData = {
        version: 2,
        timestamp: Date.now(),
        settings: [],
        contacts: [],
        items: [],
        invoices: [],
        quotes: [],
        expenses: [],
        payments: [],
        templates: [],
        inboxThreads: [],
        inboxMessages: [],
        inboxTemplates: [],
        emailTemplates: []
    };

    if (parsed.format !== 'json' || !parsed.jsonData) {
        return backup;
    }

    const data = parsed.jsonData;
    const now = Date.now();

    // --- MAP CONTACTS ---
    const rawContacts = data.clients || data.customers || data.contacts || [];
    if (Array.isArray(rawContacts)) {
        backup.contacts = rawContacts.map((c: any) => ({
            id: c.id || uuidv4(),
            type: 'client' as const,
            name: c.name || c.company || 'Sin Nombre',
            nif: c.taxId || c.vat || c.nif || '',
            email: c.email || '',
            phone: c.phone || '',
            address: c.address || '',
            city: c.city || '',
            zip: c.zip || '',
            province: c.province || '',
            country: c.country || '',
            notes: c.notes || '',
            createdAt: now,
            updatedAt: now
        }));
    }

    // --- MAP ITEMS ---
    const rawItems = data.items || data.products || data.services || [];
    if (Array.isArray(rawItems)) {
        backup.items = rawItems.map((i: any) => ({
            id: i.id || uuidv4(),
            name: i.name || i.title || 'Sin Título',
            description: i.description || '',
            price: parseFloat(i.price || i.rate || i.unitPrice || '0'),
            vatPct: parseFloat(i.tax || i.taxRate || i.vatPct || '0'),
            unit: i.unit || 'unidad',
            type: (i.type as 'service' | 'product') || 'product',
            active: i.active !== false,
            sku: i.sku || '',
            createdAt: now,
            updatedAt: now
        }));
    }

    // --- MAP INVOICES ---
    const rawInvoices = data.invoices || data.bills || [];
    if (Array.isArray(rawInvoices)) {
        backup.invoices = rawInvoices.map((inv: any) => {
            const lines = mapLineItems(inv.items || inv.lines);
            const baseTotal = parseFloat(inv.subtotal || '0');
            const vatTotal = parseFloat(inv.taxTotal || '0');
            const grandTotal = parseFloat(inv.total || '0') || (baseTotal + vatTotal);
            return {
                id: inv.id || uuidv4(),
                number: inv.number || inv.ref || 'Unknown',
                date: inv.date || new Date().toISOString().split('T')[0],
                dueDate: inv.dueDate || inv.due || '',
                status: mapStatus(inv.status, 'invoice'),
                contactId: inv.clientId || findClientIdByName(backup.contacts, inv.clientName) || '',
                lines,
                notes: inv.notes || '',
                baseTotal,
                vatTotal,
                retentionTotal: 0,
                grandTotal,
                paidAmount: 0,
                templateId: 'default',
                createdAt: now,
                updatedAt: now
            } as Invoice;
        });
    }

    // --- MAP QUOTES ---
    const rawQuotes = data.quotes || data.estimates || [];
    if (Array.isArray(rawQuotes)) {
        backup.quotes = rawQuotes.map((q: any) => {
            const lines = mapLineItems(q.items || q.lines);
            const baseTotal = parseFloat(q.subtotal || '0');
            const vatTotal = parseFloat(q.taxTotal || '0');
            const grandTotal = parseFloat(q.total || '0') || (baseTotal + vatTotal);
            return {
                id: q.id || uuidv4(),
                number: q.number || q.ref || 'Unknown',
                date: q.date || new Date().toISOString().split('T')[0],
                dueDate: q.expiryDate || q.validUntil || '',
                status: mapStatus(q.status, 'quote'),
                contactId: q.clientId || findClientIdByName(backup.contacts, q.clientName) || '',
                lines,
                notes: q.notes || '',
                baseTotal,
                vatTotal,
                retentionTotal: 0,
                grandTotal,
                templateId: 'default',
                createdAt: now,
                updatedAt: now
            } as Quote;
        });
    }

    return backup;
};

// --- HELPER FUNCTIONS ---

const mapStatus = (status: string, type: 'invoice' | 'quote'): any => {
    const s = (status || '').toLowerCase();
    if (type === 'invoice') {
        if (s.includes('paid') || s.includes('pagad')) return 'paid';
        if (s.includes('draft') || s.includes('borr')) return 'draft';
        if (s.includes('cancel')) return 'cancelled';
        return 'sent'; // default
    } else {
        if (s.includes('accept')) return 'accepted';
        if (s.includes('reject') || s.includes('rechaz')) return 'rejected';
        if (s.includes('draft') || s.includes('borr')) return 'draft';
        return 'sent';
    }
};

const mapLineItems = (items: any[]): LineItem[] => {
    if (!Array.isArray(items)) return [];
    return items.map((item: any) => ({
        id: item.id || uuidv4(),
        description: item.description || item.name || 'Item',
        quantity: parseFloat(item.quantity || item.qty || '1'),
        price: parseFloat(item.unitPrice || item.rate || item.price || '0'),
        vatPct: parseFloat(item.taxRate || item.tax || item.vatPct || '0'),
        discountPct: parseFloat(item.discountPct || item.discount || '0'),
        retentionPct: parseFloat(item.retentionPct || '0')
    }));
};

const findClientIdByName = (contacts: Contact[] | undefined, name: string): string => {
    if (!contacts || !name) return '';
    const contact = contacts.find(c => c.name.toLowerCase() === name.toLowerCase());
    return contact ? contact.id : '';
};
